import { useCallback, useEffect, useState } from 'react';
import { useSession } from '@zehnmindai/embed/react';
import baseAxios from '../utils/request';
import { AUTH_ME } from '../utils/urls';
import { holdsAnyScope, holdsScope, isBreakGlassAdmin } from './scopes.js';

// The signed-in person's scopes in docs (GET /api/v1/auth/me, through the
// SessionProvider), asked by the names in shared/auth/scopes.js (`SCOPE`).
// The break-glass admin (kind "admin") holds every scope. The functions
// change identity only when the scopes do.
//
//   const { hasScope, hasAnyScope } = useScopes();
//   hasScope(SCOPE.FILES_WRITE)
export const useScopes = () => {
  const { scopes, user } = useSession();
  const admin = isBreakGlassAdmin(user);
  const hasScope = useCallback((scope) => holdsScope(scopes, scope, { admin }), [scopes, admin]);
  const hasAnyScope = useCallback((list) => holdsAnyScope(scopes, list, { admin }), [scopes, admin]);
  return { hasScope, hasAnyScope };
};

// Whether the console is open, as docs-api says it (`GET /api/v1/auth/me` →
// `can_manage`: the caller holds `docs:admin`, the admin persona, or is the
// break-glass admin — what `get_admin_user` lets into the console). The
// SessionProvider reads the same route but keeps only the person and their
// scopes, so this asks once more, once per person and page.
const consoleAnswers = new Map(); // user uuid -> promise of can_manage

/** docs-api's `can_manage` for the signed-in person; false when it did not answer. */
export function fetchCanManage(userUuid) {
  const key = String(userUuid || '');
  if (!consoleAnswers.has(key)) {
    consoleAnswers.set(
      key,
      baseAxios({ method: 'GET', url: AUTH_ME })
        .then(({ data }) => data?.canManage === true)
        .catch(() => {
          consoleAnswers.delete(key);
          return false;
        }),
    );
  }
  return consoleAnswers.get(key);
}

/** Test seam: ask docs-api again. */
export const resetConsoleAccess = () => {
  consoleAnswers.clear();
};

/**
 * Whether the console is open to the signed-in person: docs-api's
 * `can_manage` (`docs:admin`, or the break-glass admin). `{ready, open}`;
 * `open` is false until docs-api answered.
 */
export function useConsoleAccess() {
  const { user } = useSession();
  const uuid = user?.uuid ?? null;
  const [answer, setAnswer] = useState({ uuid: null, open: false });
  useEffect(() => {
    let live = true;
    if (!uuid) return undefined;
    fetchCanManage(uuid).then((open) => {
      if (live) setAnswer({ uuid, open });
    });
    return () => {
      live = false;
    };
  }, [uuid]);
  const ready = uuid !== null && answer.uuid === uuid;
  return { ready, open: ready && answer.open };
}

export default useScopes;
